import { createApi } from '@reduxjs/toolkit/query/react';
import { captureException } from '@sentry/core';
import { baseQueryWithAuth } from './utils';
import { HSLToRGBA, hexToRgba } from '@/helpers/colors';
import { capitalize } from '@/helpers/stringConversion';
import colorbrewerDefault from 'colorbrewer/index.es.js';
import type colorbrewerType from 'colorbrewer';

// Cast the default export to the proper type for better TypeScript support
// This is all necessary because of https://github.com/saikocat/colorbrewer/issues/14#issuecomment-3032528042
const colorbrewer = colorbrewerDefault as typeof colorbrewerType;
const isColorbrewerSetSize = (
  n: number
): n is keyof typeof colorbrewer.RdYlBu => n in colorbrewer.RdYlBu;
import type {
  DISTRIBUTION_CLIENT,
  TRANSMISSION_CLIENT,
  PhaseType,
} from './types';
import { type RGBA } from '@mantine/core';
import type { WorkLogStatus } from '@/generated/types/navigator/types.gen';
import { TreesType } from '@/generated/types/navigator/types.gen';

export type Level1Details = {
  level1: string;
  level2: number[];
};

export type Species = {
  category: string;
  id: number;
  name: string;
  color?: RGBA;
};

export type Field = {
  field: string;
  url: string;
};

interface SpeciesUpdated {
  speciesGroupedByCategory: Record<string, Species[]>;
  updatedSpecies: Species[];
}

interface FieldDataByNetwork<T = string> {
  network: number;
  values: T[];
}

export const fieldApi = createApi({
  reducerPath: 'fieldApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['fieldWorkStatus'],
  endpoints: (builder) => ({
    fetchFields: builder.query<Field[], void>({
      query: () => ({
        url: '/v1/field/',
      }),
    }),
    fetchLevel1FieldData: builder.query<Record<number, Level1Details[]>, void>({
      query: () => ({
        url: '/v1/field/level1',
      }),
    }),
    /**
     * Technically, this is a Record<string, ...> but the strings are numbers,
     * so we'll leave it like this for now.
     */
    fetchNetworkFieldData: builder.query<
      Record<number, typeof TRANSMISSION_CLIENT | typeof DISTRIBUTION_CLIENT>,
      void
    >({
      query: () => ({
        url: '/v1/field/network',
      }),
    }),
    fetchVoltageInKv: builder.query<number[], void>({
      query: () => ({
        url: '/v1/field/voltageInKv',
      }),
    }),
    fetchLevel2FieldData: builder.query<string[], void>({
      query: () => ({
        url: '/v1/field/level2',
      }),
    }),
    fetchFieldSpecies: builder.query<Species[], void>({
      query: () => ({
        url: '/v1/field/species',
      }),
    }),
    fetchTrees: builder.query<FieldDataByNetwork<TreesType>[], void>({
      query: () => ({
        url: '/v1/field/trees',
      }),
    }),
    fetchWorkStatus: builder.query<WorkLogStatus[], void>({
      query: () => ({
        url: '/v1/field/workStatus',
      }),
      providesTags: () => [
        {
          type: 'fieldWorkStatus' as const,
        },
      ],
    }),
    fetchUpdatedAtYear: builder.query<string[], void>({
      query: () => ({
        url: '/v1/field/updated_at_year',
      }),
    }),
    fetchPhasingType: builder.query<PhaseType[], void>({
      query: () => ({
        url: '/v1/field/phasingType',
      }),
    }),
  }),
});

export const resetFieldApiState = fieldApi.util.resetApiState;

export const usePhasingTypes = (fields?: Field[]) => {
  const { data: phasingTypes } = useFetchPhasingTypeQuery(undefined, {
    skip: !fields || !fields.map((i) => i.field).includes('phasingType'),
  });

  const getPhasingValue = (phasing: string = '') =>
    ['single-phase', 'two-phase', 'three-phase'].findIndex(
      (value) => value === phasing
    );

  if (!phasingTypes) {
    return [];
  }
  const sortedPhasing = [...phasingTypes];
  sortedPhasing.sort(
    (p1?: string, p2?: string) => getPhasingValue(p1) - getPhasingValue(p2)
  );

  return sortedPhasing;
};

export const useVoltageInKv = (fields?: Field[]) => {
  const { data: voltageInKv } = useFetchVoltageInKvQuery(undefined, {
    skip: !fields || !fields.map((i) => i.field).includes('voltageInKv'),
  });

  if (!voltageInKv) {
    return [];
  }
  return voltageInKv;
};

export const groupBySpeciesCategory = (species?: Species[]): SpeciesUpdated => {
  if (!species) {
    return {
      speciesGroupedByCategory: {},
      updatedSpecies: [],
    };
  }

  const nonOtherCategories = [
    ...Array.from(
      new Set(
        species
          .filter((specie) => specie.category !== 'other')
          .map((s) => s.category)
      )
    ),
  ];

  let saturationFastGrowing = 100;
  let saturationHealth = 100;
  let saturationSlowGrowing = 100;
  let saturationProtected = 100;
  let lumiFast = 50;
  let lumiHealth = 50;
  let lumiSlow = 50;
  let lumiProtected = 50;
  const EVERGREEN_STROKE_COLOR = { r: 6, g: 223, b: 27, a: 80 };
  const DECIDUOUS_STROKE_COLOR = { r: 250, g: 65, b: 253, a: 80 };
  const OTHER = { r: 0, g: 0, b: 0, a: 80 };

  const mapColorsViaSLVariation = (species: Species[]): Species[] => {
    /**
     * For the given list of Species, add dynamic colors to species with non-'other' categories
     * by varying saturation and luminesence for each species in the given category. Apply
     * hardcoded colors to species with the 'other' category.
     */
    return species.map((specie: Species) => {
      const name = capitalize(specie.name); // Capitalize name for display
      let color;
      if (specie.category === 'fast_growing') {
        color = HSLToRGBA(
          310,
          saturationFastGrowing <= 15 ? 100 : (saturationFastGrowing -= 20),
          lumiFast >= 90 ? 50 : (lumiFast += 10),
          140
        );
      } else if (specie.category === 'health_issue') {
        color = HSLToRGBA(
          76,
          saturationHealth <= 15 ? 100 : (saturationHealth -= 20),
          lumiHealth >= 90 ? 100 : (lumiHealth += 10),
          140
        );
      } else if (specie.category === 'slow_growing') {
        color = HSLToRGBA(
          191,
          saturationSlowGrowing <= 15 ? 100 : (saturationSlowGrowing -= 20),
          lumiSlow >= 90 ? 98 : (lumiSlow += 10),
          54
        );
      } else if (specie.category === 'protected') {
        color = HSLToRGBA(
          126,
          saturationProtected <= 15 ? 100 : (saturationProtected -= 20),
          lumiProtected >= 90 ? 98 : (lumiProtected += 10),
          54
        );
      } else if (specie.category === 'other') {
        color = colorForOtherCategory(specie);
      }
      return color ? { ...specie, name, color } : { ...specie, name };
    });
  };

  const mapColorsViaColorbrewer = (species: Species[]): Species[] => {
    /**
     * For the given list of Species, add dynamic colors to species with non-'other' categories
     * using colorbrewer. Apply hardcoded colors to species with the 'other' category.
     */
    const speciesWithoutOther = species.filter(
      (specie) => specie.category !== 'other'
    );
    if (new Set(speciesWithoutOther.map((s) => s.category)).size > 1) {
      captureException(
        new Error(
          'colorbrewer is assumed to be used when there is only 1 non-other species category'
        )
      );
    }

    const speciesOtherColored = species
      .filter((specie) => specie.category.toLowerCase() === 'other')
      .map((specie) => {
        const name = capitalize(specie.name);
        const color = colorForOtherCategory(specie);
        return { ...specie, name, color };
      });

    const speciesWithoutOtherColored = speciesWithoutOther.map((specie, i) => {
      const name = capitalize(specie.name); // Capitalize name for display
      const numSpeciesInCat = speciesWithoutOther.length;
      let speciesHex: string | undefined;
      if (numSpeciesInCat < 3) {
        // Start with the last color in the colorbrewer set because it's the least angry
        const j = (2 + i) % 3;
        speciesHex = colorbrewer.RdYlBu[3][j];
      } else if (isColorbrewerSetSize(numSpeciesInCat)) {
        speciesHex = colorbrewer.RdYlBu[numSpeciesInCat][i];
      } else {
        captureException(
          new Error(
            'colorbrewer only supports coloring sets with cardinality <= 11'
          )
        );
      }
      return speciesHex
        ? { ...specie, name, color: hexToRgba(speciesHex) }
        : { ...specie, name };
    });

    return [...speciesOtherColored, ...speciesWithoutOtherColored];
  };

  const colorForOtherCategory = (specie: Species): RGBA => {
    const name = capitalize(specie.name);
    const color =
      name.toLocaleLowerCase() === 'other evergreen'
        ? EVERGREEN_STROKE_COLOR
        : name.toLocaleLowerCase() === 'other deciduous'
          ? DECIDUOUS_STROKE_COLOR
          : OTHER;
    return color;
  };

  let updatedSpecies;
  if (nonOtherCategories.length > 1) {
    // There are multiple non-other categories
    // Color non-other species via dynamic method 1: Varying HSL
    // saturation/luminescense off a base color
    updatedSpecies = mapColorsViaSLVariation(species);
  } else {
    // There is only a single non-other category (e.g. COMED, COPEL as of 5/8/25)
    // Use dynamic coloring method 2: colorbrewer (accessible)
    updatedSpecies = mapColorsViaColorbrewer(species);
  }

  const sortedSpecies = [...updatedSpecies];
  sortedSpecies.sort((a, b) =>
    a.category.toLowerCase() === 'other' || b.category.toLowerCase() === 'other'
      ? -1
      : a.category.toLowerCase().localeCompare(b.category.toLowerCase())
  );

  const speciesGroupedByCategory = sortedSpecies.reduce<
    Record<string, Species[]>
  >((groups, specie) => {
    groups[specie.category] = groups[specie.category] || [];
    groups[specie.category].push(specie);
    return groups;
  }, {});
  return {
    speciesGroupedByCategory: speciesGroupedByCategory,
    updatedSpecies: updatedSpecies,
  };
};

const TREE_TYPE_ORDER = [
  TreesType.HAZARD,
  TreesType.DECLINING,
  TreesType.STRIKE,
];

const sortTreeTypes = (a: TreesType, b: TreesType) => {
  return TREE_TYPE_ORDER.indexOf(a) - TREE_TYPE_ORDER.indexOf(b);
};

export const useTreeTypes = (): TreesType[] => {
  const { data: treeTypesByNetwork } = useFetchTreesQuery();
  if (treeTypesByNetwork === undefined) return [];

  return treeTypesByNetwork
    .map((i) => i.values)
    .flat()
    .filter((value, index, array) => array.indexOf(value) === index)
    .sort(sortTreeTypes);
};

export const useTreeTypesByNetwork = (network: number) => {
  const { data: treeTypes } = useFetchTreesQuery();
  const treeTypesForNetwork =
    treeTypes?.find((t) => t.network === network)?.values ?? [];
  return [...treeTypesForNetwork].sort(sortTreeTypes);
};

export const {
  useFetchLevel1FieldDataQuery,
  useFetchFieldsQuery,
  useFetchVoltageInKvQuery,
  useFetchLevel2FieldDataQuery,
  useFetchNetworkFieldDataQuery,
  useFetchFieldSpeciesQuery,
  useFetchTreesQuery,
  useFetchWorkStatusQuery,
  useFetchUpdatedAtYearQuery,
  useFetchPhasingTypeQuery,
} = fieldApi;
